<template>
  <div class="page-shell adm-user-page">
    <div class="page-header">
      <div>
        <p class="page-kicker">ACCESS CONTROL</p>
        <h2 class="page-title">人员管理</h2>
        <p class="page-subtitle">维护后台用户，配置每个人可访问的菜单权限。</p>
      </div>
      <button class="hf-btn hf-btn--primary" type="button" @click="openUserDialog()">
        <span>+</span>
        <span>新增用户</span>
      </button>
    </div>

    <section class="glass-panel table-panel">
      <div class="action-row user-toolbar">
        <button class="hf-btn" type="button" :disabled="loading" @click="loadUserPage(current, size)">
          <span>↻</span>
          <span>刷新</span>
        </button>
        <button
          class="hf-btn hf-btn--danger"
          type="button"
          :disabled="!selectedIdList.length || deleting"
          @click="deleteSelectedUsers"
        >
          删除选中
        </button>
        <span class="muted-text">已选 {{ selectedIdList.length }} 个用户</span>
      </div>

      <div class="hf-table-wrap">
        <table class="hf-table user-table">
          <thead>
            <tr>
              <th class="check-cell">
                <input
                  type="checkbox"
                  :checked="isAllCurrentPageChecked"
                  :disabled="!userList.length"
                  @change="toggleCurrentPage($event.target.checked)"
                >
              </th>
              <th>用户名</th>
              <th>手机号</th>
              <th>状态</th>
              <th>用户ID</th>
              <th>操作</th>
            </tr>
          </thead>
          <tbody v-if="userList.length">
            <tr v-for="row in userList" :key="row.id">
              <td class="check-cell">
                <input
                  type="checkbox"
                  :checked="selectedIdList.includes(row.id)"
                  @change="toggleUserSelection(row.id, $event.target.checked)"
                >
              </td>
              <td>
                <div class="user-name" :data-tooltip="row.userName || null">{{ row.userName || '-' }}</div>
              </td>
              <td :data-tooltip="row.phone || null">{{ row.phone || '-' }}</td>
              <td>
                <span class="hf-tag" :class="Number(row.isDisabled) === 1 ? 'hf-tag--danger' : 'hf-tag--success'">
                  {{ Number(row.isDisabled) === 1 ? '已禁用' : '启用中' }}
                </span>
              </td>
              <td class="id-cell" :data-tooltip="row.id || null">{{ row.id }}</td>
              <td>
                <div class="row-actions">
                  <button class="hf-btn hf-btn--text" type="button" @click="openUserDialog(row)">编辑</button>
                  <button class="hf-btn hf-btn--text" type="button" @click="openMenuDialog(row)">菜单</button>
                  <button class="hf-btn hf-btn--text danger-link" type="button" @click="deleteOneUser(row)">删除</button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
        <div v-if="!userList.length && !loading" class="hf-table-empty">暂无用户</div>
        <div v-if="loading" class="hf-loading">加载中...</div>
      </div>

      <div class="pagination-wrap">
        <div class="hf-pagination">
          <button class="hf-page-btn" type="button" :disabled="current <= 1 || loading" @click="changePage(current - 1)">‹</button>
          <button
            v-for="page in pageNumbers"
            :key="page"
            class="hf-page-btn"
            :class="{ active: page === current }"
            type="button"
            :disabled="loading"
            @click="changePage(page)"
          >{{ page }}</button>
          <button class="hf-page-btn" type="button" :disabled="current >= pageCount || loading" @click="changePage(current + 1)">›</button>
          <select class="hf-select page-size" v-model.number="size" :disabled="loading" @change="changeSize">
            <option v-for="item in [10, 20, 50, 100]" :key="item" :value="item">{{ item }} 条/页</option>
          </select>
          <span>共 {{ total }} 条</span>
        </div>
      </div>
    </section>

    <div v-if="userDialogVisible" class="hf-modal-mask">
      <section class="hf-modal">
        <div class="hf-modal__header">
          <h3 class="hf-modal__title">{{ userForm.id ? '编辑用户' : '新增用户' }}</h3>
          <button class="hf-modal__close" type="button" @click="closeUserDialog">×</button>
        </div>

        <form class="user-form" @submit.prevent="saveUser">
          <label class="hf-form-item">
            <span class="hf-label">用户名</span>
            <input class="hf-field" v-model.trim="userForm.userName" autocomplete="off" placeholder="请输入用户名">
          </label>
          <label class="hf-form-item">
            <span class="hf-label">手机号</span>
            <input class="hf-field" v-model.trim="userForm.phone" autocomplete="off" placeholder="请输入手机号">
          </label>
          <label class="hf-form-item">
            <span class="hf-label">密码</span>
            <input class="hf-field" v-model.trim="userForm.pwd" autocomplete="new-password" type="password" :placeholder="userForm.id ? '不修改可留空' : '请输入密码'">
          </label>
          <label class="hf-form-item">
            <span class="hf-label">状态</span>
            <select class="hf-select" v-model.number="userForm.isDisabled">
              <option :value="0">启用</option>
              <option :value="1">禁用</option>
            </select>
          </label>

          <div class="modal-actions">
            <button class="hf-btn" type="button" @click="closeUserDialog">取消</button>
            <button class="hf-btn hf-btn--primary" type="submit" :disabled="savingUser">
              {{ savingUser ? '保存中...' : '保存' }}
            </button>
          </div>
        </form>
      </section>
    </div>

    <div v-if="menuDialogVisible" class="hf-modal-mask">
      <section class="hf-modal hf-modal--wide">
        <div class="hf-modal__header">
          <div>
            <p class="modal-kicker">MENU ACCESS</p>
            <h3 class="hf-modal__title">{{ activeUser ? activeUser.userName : '' }} 的菜单权限</h3>
          </div>
          <button class="hf-modal__close" type="button" @click="closeMenuDialog">×</button>
        </div>

        <div class="menu-actions">
          <button class="hf-btn" type="button" :disabled="menuLoading" @click="checkAllMenus">全选</button>
          <button class="hf-btn" type="button" :disabled="menuLoading" @click="checkedMenuIdList = []">清空</button>
          <span class="muted-text">已选 {{ checkedMenuIdList.length }} 项</span>
        </div>

        <div class="menu-tree-panel">
          <div v-if="menuLoading" class="menu-loading">加载菜单中...</div>
          <div v-else-if="!menuTree.length" class="hf-table-empty">暂无可配置菜单</div>
          <ul v-else class="menu-tree">
            <menu-node
              v-for="node in menuTree"
              :key="node.id"
              :node="node"
              :checked-id-list="checkedMenuIdList"
              @toggle="toggleMenuNode"
            />
          </ul>
        </div>

        <div class="modal-actions">
          <button class="hf-btn" type="button" @click="closeMenuDialog">取消</button>
          <button class="hf-btn hf-btn--primary" type="button" :disabled="savingMenus || menuLoading" @click="saveMenus">
            {{ savingMenus ? '保存中...' : '保存菜单' }}
          </button>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import {
  addAdmUser,
  deleteAdmUser,
  listConfigurableMenus,
  listUserMenus,
  pageAdmUser,
  saveUserMenus,
  updateAdmUser
} from '@/api/AdmUser'

const MenuNode = {
  name: 'MenuNode',
  props: {
    node: {
      type: Object,
      required: true
    },
    checkedIdList: {
      type: Array,
      required: true
    }
  },
  computed: {
    descendants() {
      return collectMenuIds(this.node)
    },
    checkedDescendants() {
      return this.descendants.filter(id => this.checkedIdList.includes(id))
    },
    isChecked() {
      return this.checkedIdList.includes(this.node.id)
    },
    isIndeterminate() {
      return !this.isChecked && this.checkedDescendants.length > 0
    }
  },
  watch: {
    isIndeterminate: {
      immediate: true,
      handler(value) {
        this.$nextTick(() => {
          if (this.$refs.checkbox) {
            this.$refs.checkbox.indeterminate = value
          }
        })
      }
    }
  },
  methods: {
    handleChange(event) {
      this.$emit('toggle', this.node, event.target.checked)
    },
    forwardToggle(node, checked) {
      this.$emit('toggle', node, checked)
    }
  },
  render(h) {
    const label = h('label', { class: 'menu-check' }, [
      h('input', {
        ref: 'checkbox',
        attrs: {
          type: 'checkbox'
        },
        domProps: {
          checked: this.isChecked
        },
        on: {
          change: this.handleChange
        }
      }),
      h('span', [
        h('strong', [this.node.menuName || this.node.menuCode || this.node.id]),
        this.node.routePath ? h('small', [this.node.routePath]) : null
      ])
    ])

    const children = [label]
    if (this.node.children && this.node.children.length) {
      children.push(h('ul', { class: 'menu-tree menu-tree--child' },
        this.node.children.map(child => h('menu-node', {
          key: child.id,
          props: {
            node: child,
            checkedIdList: this.checkedIdList
          },
          on: {
            toggle: this.forwardToggle
          }
        }))
      ))
    }

    return h('li', { class: 'menu-node' }, children)
  }
}

function collectMenuIds(node) {
  const children = node.children || []
  return children.reduce((ids, child) => ids.concat(child.id, collectMenuIds(child)), [])
}

function buildMenuTree(menuList = []) {
  const sortedMenus = menuList.slice().sort((a, b) => Number(a.sortNo || 0) - Number(b.sortNo || 0))
  const nodeMap = sortedMenus.reduce((map, item) => {
    map[item.id] = {
      ...item,
      children: []
    }
    return map
  }, {})
  const tree = []
  sortedMenus.forEach((item) => {
    const node = nodeMap[item.id]
    const parent = nodeMap[item.parentId]
    if (parent) {
      parent.children.push(node)
    } else {
      tree.push(node)
    }
  })
  return tree
}

function getMenuDescendantIds(menu, menuList = []) {
  const children = menuList.filter(item => item.parentId === menu.id)
  return children.reduce((ids, child) => ids.concat(child.id, getMenuDescendantIds(child, menuList)), [])
}

function normalizeIdList(list = []) {
  return list
    .map(item => {
      if (item && typeof item === 'object') {
        return item.id !== undefined
          ? item.id
          : (item.menuId !== undefined ? item.menuId : item)
      }
      return item
    })
    .filter(item => item !== undefined && item !== null && item !== '')
    .map(item => String(item))
}

function normalizeMenuList(list = []) {
  return list.map(item => ({
    ...item,
    id: String(item.id),
    parentId: item.parentId !== undefined && item.parentId !== null ? String(item.parentId) : ''
  }))
}

export default {
  name: 'AdmUser',
  components: {
    MenuNode
  },
  data() {
    return {
      loading: false,
      deleting: false,
      savingUser: false,
      savingMenus: false,
      menuLoading: false,
      userDialogVisible: false,
      menuDialogVisible: false,
      userList: [],
      selectedIdList: [],
      total: 0,
      current: 1,
      size: 20,
      userForm: this.createEmptyUserForm(),
      allMenuList: [],
      checkedMenuIdList: [],
      activeUser: null
    }
  },
  computed: {
    pageCount() {
      return Math.max(1, Math.ceil(this.total / this.size))
    },
    pageNumbers() {
      const pages = []
      const start = Math.max(1, Math.min(this.current - 2, this.pageCount - 4))
      const end = Math.min(this.pageCount, start + 4)
      for (let i = start; i <= end; i++) {
        pages.push(i)
      }
      return pages
    },
    isAllCurrentPageChecked() {
      return this.userList.length > 0 && this.userList.every(item => this.selectedIdList.includes(item.id))
    },
    menuTree() {
      return buildMenuTree(this.allMenuList)
    }
  },
  mounted() {
    this.loadUserPage()
  },
  methods: {
    createEmptyUserForm() {
      return {
        id: '',
        userName: '',
        phone: '',
        pwd: '',
        isDisabled: 0
      }
    },
    showToast(type, title, message = '') {
      this.$toast({
        type,
        title,
        message
      })
    },
    loadUserPage(current = this.current, size = this.size) {
      this.loading = true
      return pageAdmUser(current, size).then((res) => {
        const page = res.data || {}
        this.userList = page.records || []
        this.total = Number(page.total || 0)
        this.current = Number(page.current || current || 1)
        this.size = Number(page.size || size || 20)
        this.selectedIdList = this.selectedIdList.filter(id => this.userList.some(item => item.id === id))
      }).catch((error) => {
        this.showToast('error', '用户列表加载失败', error.message || '')
      }).finally(() => {
        this.loading = false
      })
    },
    changePage(page) {
      if (page < 1 || page > this.pageCount || page === this.current) return
      this.loadUserPage(page, this.size)
    },
    changeSize() {
      this.loadUserPage(1, this.size)
    },
    toggleUserSelection(id, checked) {
      if (checked && !this.selectedIdList.includes(id)) {
        this.selectedIdList.push(id)
      }
      if (!checked) {
        this.selectedIdList = this.selectedIdList.filter(item => item !== id)
      }
    },
    toggleCurrentPage(checked) {
      const pageIds = this.userList.map(item => item.id)
      if (checked) {
        this.selectedIdList = Array.from(new Set(this.selectedIdList.concat(pageIds)))
      } else {
        this.selectedIdList = this.selectedIdList.filter(id => !pageIds.includes(id))
      }
    },
    openUserDialog(user) {
      this.userForm = user
        ? {
            id: user.id,
            userName: user.userName || '',
            phone: user.phone || '',
            pwd: '',
            isDisabled: Number(user.isDisabled || 0)
          }
        : this.createEmptyUserForm()
      this.userDialogVisible = true
    },
    closeUserDialog() {
      this.userDialogVisible = false
      this.userForm = this.createEmptyUserForm()
    },
    validateUserForm() {
      if (!this.userForm.userName) {
        this.showToast('warning', '请输入用户名')
        return false
      }
      if (!this.userForm.id && !this.userForm.pwd) {
        this.showToast('warning', '新增用户需要填写密码')
        return false
      }
      return true
    },
    saveUser() {
      if (!this.validateUserForm()) return
      const payload = { ...this.userForm }
      if (payload.id && !payload.pwd) {
        delete payload.pwd
      }
      this.savingUser = true
      const request = payload.id ? updateAdmUser(payload) : addAdmUser(payload)
      request.then((res) => {
        if (res.code === '200' || res.data) {
          this.showToast('success', payload.id ? '用户已更新' : '用户已新增')
          this.closeUserDialog()
          this.loadUserPage(this.current, this.size)
          return
        }
        this.showToast('error', '保存失败', res.msg || res.message || '')
      }).catch((error) => {
        this.showToast('error', '保存失败', error.message || '')
      }).finally(() => {
        this.savingUser = false
      })
    },
    deleteOneUser(user) {
      this.confirmDelete([user.id], `确定删除用户「${user.userName || user.id}」吗？`)
    },
    deleteSelectedUsers() {
      this.confirmDelete(this.selectedIdList, `确定删除选中的 ${this.selectedIdList.length} 个用户吗？`)
    },
    confirmDelete(idList, message) {
      this.$confirmDialog(message).then((confirmed) => {
        if (!confirmed) return
        this.deleting = true
        deleteAdmUser(idList).then((res) => {
          if (res.code === '200' && res.data !== false) {
            this.showToast('success', '删除成功')
            this.selectedIdList = this.selectedIdList.filter(id => !idList.includes(id))
            const nextPage = this.userList.length === idList.length && this.current > 1 ? this.current - 1 : this.current
            this.loadUserPage(nextPage, this.size)
            return
          }
          this.showToast('error', '删除失败', res.msg || res.message || '')
        }).catch((error) => {
          this.showToast('error', '删除失败', error.message || '')
        }).finally(() => {
          this.deleting = false
        })
      })
    },
    openMenuDialog(user) {
      this.activeUser = user
      this.checkedMenuIdList = []
      this.menuDialogVisible = true
      this.menuLoading = true
      Promise.all([
        listConfigurableMenus(),
        listUserMenus(user.id)
      ]).then(([menuRes, userMenuRes]) => {
        this.allMenuList = normalizeMenuList(menuRes.data || [])
        this.checkedMenuIdList = normalizeIdList(userMenuRes.data || [])
      }).catch((error) => {
        this.showToast('error', '菜单加载失败', error.message || '')
      }).finally(() => {
        this.menuLoading = false
      })
    },
    closeMenuDialog() {
      this.menuDialogVisible = false
      this.activeUser = null
      this.checkedMenuIdList = []
    },
    toggleMenuNode(node, checked) {
      const ancestorIds = this.getAncestorMenuIds(node)
      const ids = [node.id].concat(collectMenuIds(node))
      if (checked) {
        this.checkedMenuIdList = Array.from(new Set(this.checkedMenuIdList.concat(ids, ancestorIds)))
      } else {
        this.checkedMenuIdList = this.cleanupMenuSelection(
          this.checkedMenuIdList.filter(id => !ids.includes(id))
        )
      }
    },
    getAncestorMenuIds(menu) {
      const ancestorIds = []
      let current = menu
      while (current && current.parentId && current.parentId !== '0') {
        const parent = this.allMenuList.find(item => item.id === current.parentId)
        if (!parent) break
        ancestorIds.push(parent.id)
        current = parent
      }
      return ancestorIds
    },
    cleanupMenuSelection(idList) {
      const selectedSet = new Set(idList)
      return idList.filter((id) => {
        const menu = this.allMenuList.find(item => item.id === id)
        if (!menu) return false
        const descendantIds = getMenuDescendantIds(menu, this.allMenuList)
        const hasSelectedDescendant = descendantIds.some(childId => selectedSet.has(childId))
        return !descendantIds.length || hasSelectedDescendant
      })
    },
    checkAllMenus() {
      this.checkedMenuIdList = normalizeIdList(this.allMenuList)
    },
    saveMenus() {
      if (!this.activeUser) return
      this.savingMenus = true
      saveUserMenus(this.activeUser.id, this.checkedMenuIdList).then((res) => {
        if (res.code === '200' && res.data !== false) {
          this.showToast('success', '菜单权限已保存')
          this.closeMenuDialog()
          return
        }
        this.showToast('error', '菜单保存失败', res.msg || res.message || '')
      }).catch((error) => {
        this.showToast('error', '菜单保存失败', error.message || '')
      }).finally(() => {
        this.savingMenus = false
      })
    }
  }
}
</script>

<style scoped>
.adm-user-page {
  padding-bottom: 12px;
}

.user-toolbar {
  justify-content: flex-start;
}

.check-cell {
  width: 46px;
  text-align: center;
}

.check-cell input,
.menu-check input {
  width: 16px;
  height: 16px;
  accent-color: #101828;
  cursor: pointer;
}

.user-table {
  min-width: 860px;
}

.user-name {
  color: #101828;
  font-weight: 800;
}

.id-cell {
  max-width: 180px;
  color: #667085;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.row-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.danger-link {
  color: #b42318;
}

.pagination-wrap {
  display: flex;
  justify-content: flex-start;
  padding-top: 14px;
  overflow-x: auto;
}

.page-size {
  width: 110px;
}

.user-form {
  display: grid;
  gap: 14px;
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 18px;
}

.modal-kicker {
  margin: 0 0 4px;
  color: #667085;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: .12em;
}

.menu-actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 12px;
}

.menu-tree-panel {
  min-height: 280px;
  max-height: 58vh;
  overflow: auto;
  border: 1px solid rgba(15, 23, 42, .08);
  border-radius: 14px;
  background: rgba(248, 250, 252, .7);
  padding: 10px;
}

.menu-loading {
  display: grid;
  min-height: 220px;
  place-items: center;
  color: #667085;
  font-weight: 700;
}

.menu-tree {
  display: grid;
  list-style: none;
  margin: 0;
  padding: 0;
  gap: 6px;
}

.menu-tree--child {
  margin: 6px 0 0 24px;
}

.menu-node {
  min-width: 0;
}

.menu-check {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  border: 1px solid rgba(15, 23, 42, .06);
  border-radius: 12px;
  background: rgba(255, 255, 255, .72);
  cursor: pointer;
  padding: 10px 12px;
}

.menu-check span {
  display: grid;
  min-width: 0;
  gap: 4px;
}

.menu-check strong {
  color: #101828;
  font-size: 14px;
}

.menu-check small {
  color: #667085;
  font-size: 12px;
  overflow-wrap: anywhere;
}

@media (max-width: 760px) {
  .modal-actions {
    flex-direction: column-reverse;
  }

  .modal-actions .hf-btn {
    width: 100%;
  }
}
</style>
